import {
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import type { InputEvent as RemoteInputEvent } from '@br/protocol';
import { cx } from '../components/ui';
import type { FrameStore } from './frames';
import {
  ClickCounter,
  MoveThrottle,
  buildMouseEvent,
  buildWheelEvent,
  fitSize,
  isPassThrough,
  mapComposition,
  mapKeyDown,
  mapKeyUp,
  mapText,
  mapToDevice,
  type Size,
} from './input';

export interface ScreenCanvasProps {
  cursor?: string;
  onCopy?: () => void;
  frames: FrameStore;
  tabId?: string;
  /** Remote viewport size to lay the canvas out with until the first frame tells us */
  fallbackSize: Size;
  /** Forward pointer/keyboard input (only the controller with an open browser) */
  enabled: boolean;
  dimmed?: boolean;
  onInput: (e: RemoteInputEvent) => void;
  children?: ReactNode;
}

const BACKSPACE = { key: 'Backspace', code: 'Backspace', keyCode: 8 };
const ENTER = { key: 'Enter', code: 'Enter', keyCode: 13 };
const NO_MODS = { altKey: false, ctrlKey: false, metaKey: false, shiftKey: false };

/**
 * Draws the latest frame for `tabId` and captures input on top of it.
 * - Frames are drawn from the FrameStore in requestAnimationFrame; React never re-renders per frame.
 * - The canvas keeps the remote viewport's aspect ratio; coordinates map through `deviceWidth`.
 * - Keyboard goes through a hidden textarea so IME composition (한글) works: composition events
 *   become `input.ime`/`input.text`, ordinary keys become `input.key`.
 */
export function ScreenCanvas({
  cursor,
  onCopy,
  frames,
  tabId,
  fallbackSize,
  enabled,
  dimmed,
  onInput,
  children,
}: ScreenCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sinkRef = useRef<HTMLTextAreaElement>(null);
  const [container, setContainer] = useState<Size>({ width: 0, height: 0 });
  const [device, setDevice] = useState<Size>(fallbackSize);
  const [hasFrame, setHasFrame] = useState(false);
  const [focused, setFocused] = useState(false);

  const onInputRef = useRef(onInput);
  const onCopyRef = useRef(onCopy);
  onCopyRef.current = onCopy;
  onInputRef.current = onInput;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const tabIdRef = useRef(tabId);
  tabIdRef.current = tabId;
  const deviceRef = useRef(device);
  deviceRef.current = device;
  const clicks = useRef(new ClickCounter());
  const throttle = useRef(new MoveThrottle(16));
  const lastClickCount = useRef(1);

  useEffect(() => {
    const moves = throttle.current;
    return () => moves.cancel();
  }, [tabId, enabled]);

  // Container size
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const r = entries[0]?.contentRect;
      if (r) setContainer({ width: r.width, height: r.height });
    });
    ro.observe(el);
    setContainer({ width: el.clientWidth, height: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  // Without a frame, lay out with the browser profile's window size
  const fw = fallbackSize.width;
  const fh = fallbackSize.height;
  useEffect(() => {
    if (!hasFrame) setDevice({ width: fw, height: fh });
  }, [fw, fh, hasFrame]);

  // Drawing
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let raf = 0;
    const draw = () => {
      raf = 0;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const info = tabId ? frames.get(tabId) : undefined;
      if (!info) {
        if (canvas.width !== 1 || canvas.height !== 1) {
          canvas.width = 1;
          canvas.height = 1;
        }
        ctx.clearRect(0, 0, 1, 1);
        setHasFrame(false);
        return;
      }
      const { bitmap, header } = info;
      try {
        if (canvas.width !== bitmap.width || canvas.height !== bitmap.height) {
          canvas.width = bitmap.width;
          canvas.height = bitmap.height;
        }
        ctx.drawImage(bitmap, 0, 0);
      } catch {
        return; // bitmap was closed under us; the next frame will redraw
      }
      const dw = header.meta.deviceWidth;
      const dh = header.meta.deviceHeight;
      if (dw > 0 && dh > 0 && (deviceRef.current.width !== dw || deviceRef.current.height !== dh))
        setDevice({ width: dw, height: dh });
      setHasFrame(true);
    };
    const schedule = () => {
      if (!raf) raf = requestAnimationFrame(draw);
    };
    schedule();
    const unsubscribe = tabId ? frames.subscribe(tabId, schedule) : undefined;
    return () => {
      unsubscribe?.();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [frames, tabId]);

  const css = fitSize(container, device);

  // ---------------------------------------------------------------- pointer

  const devicePoint = (e: { clientX: number; clientY: number }) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return mapToDevice({ x: e.clientX, y: e.clientY }, rect, deviceRef.current);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    throttle.current.cancel();
    e.preventDefault();
    sinkRef.current?.focus({ preventScroll: true });
    const tab = tabIdRef.current;
    if (!enabledRef.current || !tab) return;
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* not supported */
    }
    const p = devicePoint(e);
    const count = clicks.current.next(e.timeStamp, p.x, p.y, e.button);
    lastClickCount.current = count;
    onInputRef.current(buildMouseEvent('mousePressed', tab, p, e, count));
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const tab = tabIdRef.current;
    if (!enabledRef.current || !tab) return;
    const event = buildMouseEvent('mouseMoved', tab, devicePoint(e), e, 0);
    throttle.current.schedule(() => {
      if (enabledRef.current && tabIdRef.current === tab) onInputRef.current(event);
    });
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    throttle.current.cancel();
    const tab = tabIdRef.current;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    if (!enabledRef.current || !tab) return;
    onInputRef.current(
      buildMouseEvent('mouseReleased', tab, devicePoint(e), e, lastClickCount.current),
    );
  };

  // Wheel needs a non-passive listener to be cancellable
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const tab = tabIdRef.current;
      if (!enabledRef.current || !tab) return;
      onInputRef.current(buildWheelEvent(tab, devicePoint(e), e, deviceRef.current.height));
    };
    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, []);

  // ---------------------------------------------------------------- keyboard / IME

  useEffect(() => {
    const el = sinkRef.current;
    if (!el) return;
    let composing = false;
    const active = () => (enabledRef.current ? tabIdRef.current : undefined);
    const send = (ev: RemoteInputEvent | null) => {
      if (ev) onInputRef.current(ev);
    };
    const tapKey = (tab: string, k: { key: string; code: string; keyCode: number }) => {
      send(mapKeyDown(tab, { ...k, ...NO_MODS }));
      send(mapKeyUp(tab, { ...k, ...NO_MODS }));
    };

    const onKeyDown = (e: KeyboardEvent) => {
      const tab = active();
      if (!tab) return;
      if (
        (e.ctrlKey || e.metaKey) &&
        !e.shiftKey &&
        e.key.toLowerCase() === 'c' &&
        onCopyRef.current
      ) {
        e.preventDefault();
        onCopyRef.current();
        return;
      }
      if (isPassThrough(e)) return;
      e.preventDefault();
      send(mapKeyDown(tab, e));
    };
    const onKeyUp = (e: KeyboardEvent) => {
      const tab = active();
      if (!tab) return;
      if (
        (e.ctrlKey || e.metaKey) &&
        !e.shiftKey &&
        e.key.toLowerCase() === 'c' &&
        onCopyRef.current
      ) {
        e.preventDefault();
        return;
      }
      if (isPassThrough(e)) return;
      e.preventDefault();
      send(mapKeyUp(tab, e));
    };
    const onCompositionStart = () => {
      composing = true;
    };
    const onCompositionUpdate = (e: CompositionEvent) => {
      const tab = active();
      if (tab) send(mapComposition(tab, e.data ?? ''));
    };
    const onCompositionEnd = (e: CompositionEvent) => {
      composing = false;
      const tab = active();
      if (tab) send(mapText(tab, e.data ?? ''));
      el.value = '';
    };
    const onBeforeInput = (e: InputEvent) => {
      if (composing || e.isComposing) return; // the IME owns the textarea while composing
      e.preventDefault();
      const tab = active();
      if (!tab) return;
      switch (e.inputType) {
        case 'insertText':
        case 'insertFromPaste':
        case 'insertFromDrop': {
          const text = e.data ?? e.dataTransfer?.getData('text/plain') ?? '';
          if (text) send(mapText(tab, text));
          return;
        }
        case 'insertLineBreak':
        case 'insertParagraph':
          tapKey(tab, ENTER);
          return;
        case 'deleteContentBackward':
          tapKey(tab, BACKSPACE);
          return;
        default:
          return;
      }
    };
    const onInputEvent = () => {
      if (!composing) el.value = '';
    };
    const onPaste = (e: ClipboardEvent) => {
      const tab = active();
      if (!tab) return;
      e.preventDefault();
      const text = e.clipboardData?.getData('text/plain');
      if (text) send(mapText(tab, text));
    };

    el.addEventListener('paste', onPaste);
    el.addEventListener('keydown', onKeyDown);
    el.addEventListener('keyup', onKeyUp);
    el.addEventListener('compositionstart', onCompositionStart);
    el.addEventListener('compositionupdate', onCompositionUpdate);
    el.addEventListener('compositionend', onCompositionEnd);
    el.addEventListener('beforeinput', onBeforeInput);
    el.addEventListener('input', onInputEvent);
    return () => {
      el.removeEventListener('paste', onPaste);
      el.removeEventListener('keydown', onKeyDown);
      el.removeEventListener('keyup', onKeyUp);
      el.removeEventListener('compositionstart', onCompositionStart);
      el.removeEventListener('compositionupdate', onCompositionUpdate);
      el.removeEventListener('compositionend', onCompositionEnd);
      el.removeEventListener('beforeinput', onBeforeInput);
      el.removeEventListener('input', onInputEvent);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="relative flex h-full w-full items-center justify-center overflow-hidden bg-screen"
    >
      <canvas
        ref={canvasRef}
        width={1}
        height={1}
        style={{
          width: css.width,
          height: css.height,
          cursor: enabled ? (cursor ?? 'default') : 'not-allowed',
        }}
        className={cx(
          'block bg-black touch-none select-none transition-opacity',
          dimmed && 'opacity-40',
          enabled ? 'cursor-default' : 'cursor-not-allowed',
        )}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onContextMenu={(e) => e.preventDefault()}
        aria-label="원격 브라우저 화면"
      />
      <textarea
        ref={sinkRef}
        className="viewer-keyboard-sink"
        aria-label="원격 키보드 입력"
        tabIndex={-1}
        autoCapitalize="off"
        autoCorrect="off"
        autoComplete="off"
        spellCheck={false}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      {enabled && focused && (
        <span className="pointer-events-none absolute left-2 top-2 rounded-full bg-black/60 px-2 py-0.5 text-[11px] text-white">
          키보드 연결됨
        </span>
      )}
      {children}
    </div>
  );
}
